import React from 'react';
import { Box, keyframes, useColorModeValue } from '@chakra-ui/react';
import { useAppearance } from '../../theme/AppearanceProvider';

// Slow, organic drift — three blobs on different paths/tempos so the field
// never visibly repeats. Calm, not flashy (Aurora Glass).
const drift = (a: string, b: string) => keyframes`
  0%   { transform: translate3d(0, 0, 0) scale(1); }
  50%  { transform: translate3d(${a}, ${b}, 0) scale(1.25); }
  100% { transform: translate3d(0, 0, 0) scale(1); }
`;

const blobs = [
  {
    top: '-12%',
    left: '-8%',
    size: '46vw',
    anim: drift('22%', '14%'),
    dur: '26s',
    token: 'brandGreen' as const,
  },
  {
    top: '-18%',
    right: '-10%',
    size: '40vw',
    anim: drift('-18%', '20%'),
    dur: '32s',
    token: 'brandBlue' as const,
  },
  {
    bottom: '-22%',
    left: '28%',
    size: '42vw',
    anim: drift('14%', '-16%'),
    dur: '38s',
    token: 'brandGreen' as const,
  },
];

/**
 * The living backdrop behind the dashboard: blurred brand-colored blobs slowly
 * drifting on the ambient canvas. Vivid in dark mode, a whisper in light.
 * pointer-events: none so it never eats clicks; clipped to its parent.
 */
const AuroraBackground: React.FC = () => {
  // item 406 — same gate as HeroGlow (item 330): animating scale on three ~45vw
  // blur(90px) blobs re-rasterizes the blur EVERY frame, forever. `motion` is false
  // under OS reduced-motion AND reduced-effects mode; static blobs raster once.
  const { aurora, motion } = useAppearance();
  // item 155 — capped (was 0.3/0.42) so a saturated brand accent (e.g. red) doesn't
  // blanket the content behind translucent panels. The accent should accent, not wash.
  const opacity = useColorModeValue(0.26, 0.34);
  const colors: Record<'brandGreen' | 'brandBlue', string> = {
    brandGreen: useColorModeValue('var(--chakra-colors-brandGreen-300)', 'var(--chakra-colors-brandGreen-400)'),
    brandBlue: useColorModeValue('var(--chakra-colors-brandBlue-300)', 'var(--chakra-colors-brandBlue-500)'),
  };

  // Accountant skin: no living backdrop — the page sits on the flat app canvas.
  if (!aurora) return null;
  // item 732 (Chad) — TEMP experiment: hide the aurora gradient to preview a flat look. Flip to
  // false to bring the glow back.
  const HIDE_GRADIENT = true;
  if (HIDE_GRADIENT) return null;

  return (
    <Box
      aria-hidden
      position="absolute"
      inset={0}
      overflow="hidden"
      pointerEvents="none"
      zIndex={0}
      // rounded top so the glow reads as a panel that sits cleanly UNDER the KPI
      // strip (item 96) — rounding the glow box, not the shell, keeps grid popovers
      // from being clipped
      borderTopRadius={{ base: '2xl', md: '3xl' }}
      sx={{ maskImage: 'radial-gradient(120% 120% at 50% 0%, #000 55%, transparent 100%)' }}
    >
      {blobs.map((b, i) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          position="absolute"
          top={b.top}
          left={b.left}
          right={b.right}
          bottom={b.bottom}
          w={b.size}
          h={b.size}
          opacity={opacity}
          bg={colors[b.token]}
          borderRadius="full"
          filter="blur(90px)"
          willChange="transform"
          animation={motion ? `${b.anim} ${b.dur} ease-in-out infinite` : undefined}
        />
      ))}
    </Box>
  );
};

export default AuroraBackground;
